import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, CheckCircle2, AlertTriangle, XCircle, Info, Trash2 } from "lucide-react";
import BackButton from "../components/BackButton";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";

const SEV_ICON = { info: Info, warning: AlertTriangle, error: XCircle, success: CheckCircle2 };
const SEV_COLOR = {
  info: "text-sky-600 bg-sky-50",
  warning: "text-amber-700 bg-amber-50",
  error: "text-red-700 bg-red-50",
  success: "text-emerald-700 bg-emerald-50",
};

export default function NotificationsPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyUnread, setOnlyUnread] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.notifList({ unread_only: onlyUnread, limit: 100 });
      setItems(r || []);
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [onlyUnread]);

  async function readAll() { await api.notifReadAll(); toast.success("Segnate lette"); load(); }
  async function del(n) {
    if (!window.confirm("Eliminare questa notifica?")) return;
    await api.notifDelete(n.id);
    load();
  }
  async function open(n) {
    if (!n.read_at) await api.notifRead(n.id);
    if (n.link_url) nav(n.link_url);
    else load();
  }

  return (
    <div className="space-y-4" data-testid="notif-page">
      <BackButton to="/" testid="notif-back-btn" />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Bell size={22} className="opacity-70" />
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">Notifiche</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch checked={onlyUnread} onCheckedChange={setOnlyUnread} data-testid="notif-filter-unread" />
            <Label className="text-xs">Solo da leggere</Label>
          </div>
          <Button variant="outline" size="sm" onClick={readAll} data-testid="notif-page-read-all">
            Segna tutte lette
          </Button>
        </div>
      </div>

      <div className="pxos-card p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center opacity-70">Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center opacity-70" data-testid="notif-page-empty">
            Nessuna notifica.
          </div>
        ) : (
          <ul>
            {items.map((n) => {
              const Icon = SEV_ICON[n.severity] || Info;
              const color = SEV_COLOR[n.severity] || "text-slate-600 bg-slate-50";
              return (
                <li key={n.id} className="p-4 border-b flex items-start gap-3"
                    style={{ borderColor: "var(--pxos-border)" }}
                    data-testid={`notif-row-${n.id}`}>
                  <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${color}`}>
                    <Icon size={16} />
                  </div>
                  <button className="text-left flex-1 min-w-0" onClick={() => open(n)}>
                    <div className="font-medium flex items-center gap-2">
                      {n.title}
                      {!n.read_at && <Badge variant="outline" className="text-[10px]">Nuova</Badge>}
                    </div>
                    {n.body && <div className="text-sm opacity-80 mt-0.5 line-clamp-3">{n.body}</div>}
                    <div className="text-xs opacity-60 mt-1">
                      {new Date(n.created_at).toLocaleString("it-IT")}
                    </div>
                  </button>
                  <Button variant="ghost" size="icon" onClick={() => del(n)}>
                    <Trash2 size={14} />
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
